import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Supplier } from '@generated/models';
import { SupplierIconComponent } from '@web/features/suppliers/components/libs/supplier-icon.component';
import { TagModule } from 'primeng/tag';
import { SupplierLinkPipe } from '../pipes/supplier-link.pipe';

@Component({
  selector: 'app-supplier-code-tag',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TagModule, RouterModule, NgTemplateOutlet, SupplierLinkPipe, SupplierIconComponent],
  template: `
    @let _size = size();
    @let _supplier = supplier();
    @let _clickable = clickable();

    @if (_clickable) {
      <a [routerLink]="_supplier | supplierLink" (click)="onClick($event)">
        <ng-container [ngTemplateOutlet]="tag" />
      </a>
    } @else {
      <ng-container [ngTemplateOutlet]="tag" />
    }

    <ng-template #tag>
      <p-tag class="h-6 w-26">
        <div class="flex items-center gap-1 px-1.5 whitespace-nowrap">
          <app-supplier-icon [kind]="supplier().kind" [class]="_size === 'small' ? '!text-3xs' : '!text-xs'" />
          <span [class]="_size === 'small' ? '!text-2xs' : '!text-sm'" class="font-bold">
            {{ _supplier.code }}
          </span>
        </div>
      </p-tag>
    </ng-template>
  `,
  styles: [
    `
      .p-tag {
        border: 1px solid var(--color-soe-ocean-blue);
        box-shadow: 2px 1px 0px 0px var(--color-soe-ocean-blue);
        --p-tag-primary-background: var(--color-white);
        padding: 0;
        --p-tag-primary-color: var(--color-black);
      }
    `,
  ],
})
export class SupplierCodeTagComponent {
  readonly size = input<'small' | 'default'>('default');
  readonly clickable = input<boolean>(true);
  readonly supplier = input.required<
    Pick<Supplier, 'id' | 'kind' | 'code'>,
    { id: Supplier['id']; kind?: Supplier['kind']; code: Supplier['code'] }
  >({
    transform: (value): Pick<Supplier, 'id' | 'kind' | 'code'> => {
      if (value.kind === undefined) {
        const kind = value.code.startsWith('SC') ? 'company' : 'freelance';
        return {
          ...value,
          kind,
        };
      }
      return {
        id: value.id,
        kind: value.kind,
        code: value.code,
      };
    },
  });

  readonly icon = computed<string>(() => {
    const supplier = this.supplier();
    const icons: Record<Supplier['kind'], string> = {
      company: 'pi pi-building-columns',
      freelance: 'pi pi-user',
    };
    return icons[supplier.kind];
  });

  onClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }
}
